import React, { FC, forwardRef, useContext, useMemo } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { Badge, Button, useMediaQuery } from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import Box from '@material-ui/core/Box';
import WorkIcon from '@material-ui/icons/Work';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import SettingsIcon from '@material-ui/icons/Settings';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import ForumIcon from '@material-ui/icons/Forum';
import ChatIcon from '@material-ui/icons/Chat';
import Logo from '../../../common/components/Logo';
import { Operation, Resource } from '../../../common/constants';
import { IUserContext, UserContext } from '../../../providers/UserProvider';
import {
  useGetAllMessageUnseen,
  useGetAllMessageUnseenDiscussion,
} from '../../../hooks/queries/useGetAllMessageUnseen';
import { drawerWidth } from '../Layout';
import { useSubscription } from '@apollo/react-hooks';
import { LISTEN_TO_GLOBAL_NEW_MESSAGE } from '../../../graphql/discussion/subscription';
import { GlobalNewMessageToDiscussion } from '../../../graphql/discussion/types/GlobalNewMessageToDiscussion';

const useStyles = makeStyles((theme) =>
  createStyles({
    active: {
      fontWeight: theme.typography.fontWeightMedium,

      '& $icon': {
        color: theme.palette.secondary.main,
      },

      '& $title': {
        color: theme.palette.secondary.main,
      },
    } as any,
    title: {
      marginLeft: '5px',
      color: theme.palette.text.primary,

      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      color: theme.palette.primary.main,

      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    drawerPaper: {
      width: drawerWidth,
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    sideBarButton: {
      padding: '10px 30px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      '&:hover': { backgroundColor: 'transparent' },
    },
    listItem: {
      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    },
    gutter: {
      padding: 0,
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
      justifyContent: 'flex-end',
    },
  }),
);

interface IPage {
  title: string;
  href: string;
  icon: any;
  resource?: Resource;
}

interface ISidebar {
  drawerOpened: boolean;
  onCloseDrawer: () => void;
}

const Sidebar: FC<ISidebar> = (props) => {
  const { drawerOpened, onCloseDrawer } = props;
  const theme = useTheme();
  //@ts-ignore
  const classes = useStyles();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const { canI } = useContext<IUserContext>(UserContext);

  const { data: allMessageUnseen, refetch: refectchMessageUnseen } =
    useGetAllMessageUnseen();
  const { data: allDiscussion, refetch: refectchDiscussion } =
    useGetAllMessageUnseenDiscussion();
  const pages: IPage[] = [
    {
      title: 'Enseignes',
      href: '/enseignes',
      icon: <AccountBalanceIcon fontSize="large" />,
      resource: Resource.ENSEIGNES,
    },
    {
      title: 'Entites',
      href: '/entites',
      icon: <AccountTreeIcon fontSize="large" />,
      resource: Resource.ENTITIES,
    },
    {
      title: 'Utilisateurs',
      href: '/users',
      icon: <GroupIcon fontSize="large" />,
      resource: Resource.USERS,
    },
    {
      title: 'Affaires',
      href: '/businesses',
      icon: <WorkIcon fontSize="large" />,
      resource: Resource.BUSINESSES,
    },
    {
      title: 'Discussions',
      href: '/allDiscussions',
      icon: (
        <Badge
          badgeContent={allDiscussion?.allMessageUnseen ?? 0}
          color="error"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          max={9}>
          <ChatIcon fontSize="large" />
        </Badge>
      ),
      resource: Resource.DISCUSSIONS,
    },
    {
      title: 'Feedbacks',
      href: '/discussions',
      icon: (
        <Badge
          badgeContent={allMessageUnseen?.allMessageUnseen ?? 0}
          color="error"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          max={9}>
          <ForumIcon fontSize="large" />
        </Badge>
      ),
      resource: Resource.FEEDBACKS,
    },
    {
      title: 'Campagnes',
      href: '/compaigns',
      icon: <ChatBubbleIcon fontSize="large" />,
      resource: Resource.COMPAIGNS,
    },
    {
      title: 'Taux',
      href: '/parameters',
      icon: <SettingsIcon fontSize="large" />,
      resource: Resource.RATES,
    },
  ];

  const CustomRouterLink = useMemo(
    () =>
      forwardRef((props, ref) => (
        //@ts-ignore
        <RouterLink
          {...props}
          onClick={async () => {
            await refectchMessageUnseen();
            await refectchDiscussion();
          }}
        />
      )),
    [],
  );
  useSubscription<GlobalNewMessageToDiscussion>(LISTEN_TO_GLOBAL_NEW_MESSAGE, {
    onSubscriptionData({ client, subscriptionData }) {
      refectchDiscussion();
    },
  });
  const drawer = (
    <List>
      {pages.map((page) => {
        const disabled: boolean =
          !!page.resource && !canI(Operation.READ, page.resource);
        if (!disabled)
          return (
            <ListItem
              disabled={disabled}
              key={page.title}
              classes={{ gutters: classes.gutter }}
              className={classes.listItem}>
              <Button
                disabled={disabled}
                //@ts-ignore
                activeClassName={classes.active}
                fullWidth
                disableRipple
                component={CustomRouterLink}
                to={page.href}
                classes={{ root: classes.sideBarButton }}>
                <div className={classes.icon}>{page.icon}</div>
                <div className={classes.title}>{page.title}</div>
              </Button>
            </ListItem>
          );
      })}
    </List>
  );

  return (
    <nav aria-label="mailbox folders">
      {/** The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        anchor="left"
        open={drawerOpened}
        className={classes.drawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        classes={{
          paper: classes.drawerPaper,
        }}>
        <Box className={classes.drawerHeader}>
          <Logo height={48} />
          <IconButton onClick={onCloseDrawer}>
            {theme.direction === 'ltr' ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </Box>
        <Divider />
        {drawer}
      </Drawer>
    </nav>
  );
};
export default Sidebar;
